// Shifts & sleep: night/early exposure, sleep vs target, shift-biometric table.
// Port of the prototype's renderShifts().

import React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { cn } from '@/lib/utils';
import { GROUPS, REGIONS, WORKERS, sign, wArr, workersInRegion } from '../engine/data';
import type { Worker } from '../engine/data';
import { genInsight } from '../engine/insights';
import { cdiLabel, cdiTone, lrmTone } from '../engine/scores';
import { AiInsight } from '../AiPanels';
import { AXIS_PROPS, GRID_PROPS, NO_ANIM, TOOLTIP_PROPS } from '../chartTheme';
import { scopedWorkers, useDash } from '../state';
import { Grid, Kpi, KpiRow, Panel, Pill, SecHeader, dataTable, fedTone, toneText } from '../ui';
import { MiniStat, RegionCardHeader, ScopePickers, signed, thresholdTone } from './ForwardScreen';

const T = dataTable;

const mean = (xs: number[]): number => xs.reduce((a, v) => a + v, 0) / xs.length;
const avgSleep = (w: Worker): number => (w.slp7 ? mean(w.slp7) : w.slp);
const isNightOrEarly = (w: Worker): boolean => w.shifts.some((s) => s.in && (s.in >= '22:00' || s.in <= '06:00'));
const avgOf = (ws: Worker[], f: (w: Worker) => number): number => (ws.length ? +mean(ws.map(f)).toFixed(1) : 0);

const SHIFT_SLEEP = ['<40h', '40–45h', '46–50h', '51–55h', '>55h'].map((label, i) => ({
  label,
  sleep: [7.1, 6.8, 6.2, 5.6, 4.8][i],
}));
const CIRCADIAN = ['05:00–06:00', '06:00–08:00', '08:00–10:00', '22:00–00:00'].map((label, i) => ({
  label,
  sleep: [5.2, 6.1, 7.0, 5.6][i],
}));

const SleepBars: React.FC<{ data: { label: string; sleep: number }[]; color: string; yMax: number }> = ({
  data,
  color,
  yMax,
}) => (
  <div className='h-[200px]'>
    <ResponsiveContainer width='100%' height='100%'>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid {...GRID_PROPS} />
        <XAxis dataKey='label' {...AXIS_PROPS} />
        <YAxis domain={[3, yMax]} width={36} {...AXIS_PROPS} />
        <Tooltip {...TOOLTIP_PROPS} formatter={(v) => [`${String(v)}h`, 'Avg sleep']} />
        <Bar dataKey='sleep' fill={color} radius={[3, 3, 0, 0]} {...NO_ANIM} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const ShiftsKpis: React.FC<{ workers: Worker[] }> = ({ workers }) => {
  const nightW = workers.filter(isNightOrEarly);
  const avgSlpAll = avgOf(workers, avgSleep);
  const sleepShort = workers.filter((w) => avgSleep(w) < 5);
  const consec5 = workers.filter((w) => w.consec >= 5).length;
  return (
    <KpiRow>
      <Kpi
        label='Night/Early Workers'
        value={nightW.length}
        sub='Circadian risk group'
        tone={nightW.length ? 'warn' : 'good'}
      />
      <Kpi
        label='Avg Sleep vs Target'
        value={`${avgSlpAll < 7 ? '−' : '+'}${Math.abs(avgSlpAll - 7).toFixed(1)}h`}
        sub={`Mean ${avgSlpAll}h vs 7.0h`}
        tone={avgSlpAll < 6 ? 'warn' : 'good'}
      />
      <Kpi
        label='Sleep <5h Incidents'
        value={sleepShort.length}
        sub='This week'
        subTrend={sleepShort.length ? 'up' : undefined}
        tone={sleepShort.length ? 'bad' : 'good'}
      />
      <Kpi label='≥5 Consec Days' value={consec5} tone={consec5 ? 'warn' : 'good'} />
    </KpiRow>
  );
};

const ShiftTable: React.FC<{ workers: Worker[] }> = ({ workers }) => {
  const { drillWorker } = useDash();
  const rows = [...workers].sort((a, b) => b.cdi - a.cdi);
  return (
    <Panel
      title='Shift–Biometric Analysis'
      sub='CDI measures shift effect on body clock · FED/LRM show readiness vs demand'
    >
      <div className={T.wrap}>
        <table className={T.table}>
          <thead>
            <tr>
              <th className={T.th}>Worker</th>
              <th className={T.th}>Type</th>
              {['Wk Hrs', 'Consec', 'CDI', 'FED', 'LRM', 'Avg Sleep', 'Sleep Δ'].map((h) => (
                <th key={h} className={cn(T.th, T.num)}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((w) => {
              const avgSlp = w.slp7 ? +mean(w.slp7).toFixed(1) : w.slp;
              const slpDelta = +(avgSlp - w.slp_b).toFixed(1);
              const isNight = w.shifts.some((s) => s.in && s.in >= '22:00');
              const ht = thresholdTone(w.shiftHrs, 48, 55);
              const ct = thresholdTone(w.consec, 4, 6);
              return (
                <tr key={w.init} className={cn(T.tr, 'cursor-pointer')} onClick={() => drillWorker(w.init)}>
                  <td className={T.td}>
                    <strong>{w.name}</strong>
                  </td>
                  <td className={T.td}>
                    <Pill tone={isNight ? 'info' : 'muted'}>{isNight ? 'Night' : 'Day'}</Pill>
                  </td>
                  <td className={cn(T.td, T.num, ht && toneText[ht])}>{w.shiftHrs}h</td>
                  <td className={cn(T.td, T.num, ct && toneText[ct])}>{w.consec}</td>
                  <td className={cn(T.td, T.num, 'font-semibold', toneText[cdiTone(w.cdi)])}>
                    {w.cdi}
                    <span className='ml-0.5 text-[10px] font-normal text-muted-foreground'>{cdiLabel(w.cdi)}</span>
                  </td>
                  <td className={cn(T.td, T.num, 'font-semibold', toneText[fedTone(w.fed)])}>{w.fed}</td>
                  <td className={cn(T.td, T.num, 'font-semibold', toneText[lrmTone(w.lrm)])}>{signed(w.lrm)}</td>
                  <td className={cn(T.td, T.num)}>{avgSlp}h</td>
                  <td
                    className={cn(
                      T.td,
                      T.num,
                      slpDelta <= -1.5 ? 'text-destructive' : slpDelta <= -0.8 ? 'text-warning' : 'text-success',
                    )}
                  >
                    {sign(slpDelta)}
                    {slpDelta}h
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

const CompanyView: React.FC = () => {
  const d = useDash();
  const allW = wArr();
  return (
    <div className='space-y-4'>
      <ShiftsKpis workers={allW} />
      <AiInsight>{genInsight('shifts', allW, { isCompany: true, scopeName: 'the company' })}</AiInsight>
      <div>
        <SecHeader>Region Shift &amp; Sleep Summary — click to view detail</SecHeader>
        <Grid cols={2}>
          {Object.entries(REGIONS).map(([rid, reg]) => {
            const rw = workersInRegion(rid);
            const rNight = rw.filter(isNightOrEarly).length;
            const rAvgSlp = avgOf(rw, avgSleep);
            const rConsec = rw.filter((w) => w.consec >= 5).length;
            const rAvgHrs = avgOf(rw, (w) => w.shiftHrs);
            return (
              <Panel key={rid} className='cursor-pointer transition-colors hover:bg-muted/40'>
                <div onClick={() => d.drillRegion(rid)}>
                  <RegionCardHeader color={reg.color} name={reg.name} count={rw.length} />
                  <div className='grid grid-cols-2 gap-2'>
                    <MiniStat value={rNight} label='Night workers' tone={rNight ? 'violet' : undefined} />
                    <MiniStat
                      value={`${rAvgSlp}h`}
                      label='Avg sleep'
                      tone={rAvgSlp < 6 ? 'bad' : rAvgSlp < 6.5 ? 'warn' : 'good'}
                    />
                    <MiniStat value={rConsec} label='≥5 consec' tone={rConsec ? 'warn' : undefined} />
                    <div className='rounded-lg border border-transparent bg-muted p-2 text-center'>
                      <div className='text-xl font-bold tabular-nums'>{rAvgHrs}h</div>
                      <div className='text-[10px] text-muted-foreground'>Avg wk hrs</div>
                    </div>
                  </div>
                </div>
              </Panel>
            );
          })}
        </Grid>
      </div>
      <Grid cols={2}>
        <Panel title='Shift Length vs Sleep Quality' sub='Longer shifts correlate with shorter sleep'>
          <SleepBars data={SHIFT_SLEEP} color='var(--color-info)' yMax={8} />
        </Panel>
        <Panel title='Circadian Disruption' sub='Avg sleep by shift start time bucket'>
          <SleepBars data={CIRCADIAN} color='var(--color-violet)' yMax={8.5} />
        </Panel>
      </Grid>
    </div>
  );
};

export const ShiftsScreen: React.FC = () => {
  const d = useDash();
  if (d.drillScope === 'company') return <CompanyView />;

  const workers = scopedWorkers(d);
  let scopeName = '';
  let regionId: string | undefined;
  if (d.drillScope === 'region' && d.activeRegion) {
    scopeName = REGIONS[d.activeRegion].name;
    regionId = d.activeRegion;
  } else if (d.drillScope === 'group' && d.activeGroup !== null) {
    scopeName = GROUPS[d.activeGroup].name;
  } else if (d.activeWorker) {
    scopeName = WORKERS[d.activeWorker].name;
  }

  return (
    <div className='space-y-4'>
      <ScopePickers />
      {/* Prototype shows KPIs for region and group scope only */}
      {d.drillScope !== 'individual' && <ShiftsKpis workers={workers} />}
      <AiInsight>{genInsight('shifts', workers, { regionId, scopeName })}</AiInsight>
      {d.drillScope === 'region' && (
        <Grid cols={2}>
          <Panel title='Shift Length vs Sleep'>
            <SleepBars data={SHIFT_SLEEP} color='var(--color-info)' yMax={8} />
          </Panel>
          <Panel title='Circadian Disruption'>
            <SleepBars data={CIRCADIAN} color='var(--color-violet)' yMax={8.5} />
          </Panel>
        </Grid>
      )}
      <ShiftTable workers={workers} />
    </div>
  );
};
